import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import PersonaSelector from '../../components/dashboard/PersonaSelector';
import ConnectedChannels from '../../components/settings/ConnectedChannels';
import CalendarSettings from '../../components/settings/CalendarSettings';
import DeleteAccountSection from '../../components/settings/DeleteAccountSection';
import {
  ArrowLeft,
  Building2,
  Briefcase,
  Loader2,
  Check,
  Settings as SettingsIcon,
  Compass,
  Target,
  TrendingUp,
  RefreshCw,
  Mail,
  Calendar,
  Clock,
  MessageSquare,
} from 'lucide-react';

const SettingsPage = () => {
  const { user, isAuthenticated, loading: authLoading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  // Motivation fields
  const [aspirations, setAspirations] = useState('');
  const [goals, setGoals] = useState('');
  const [growthAreas, setGrowthAreas] = useState('');
  const [savingMotivation, setSavingMotivation] = useState(false);
  const [savedMotivation, setSavedMotivation] = useState(false);

  // Persona
  const [showPersonaSelector, setShowPersonaSelector] = useState(false);

  // Email Preferences
  const [emailFrequency, setEmailFrequency] = useState('daily');
  const [savingEmail, setSavingEmail] = useState(false);
  const [savedEmail, setSavedEmail] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      setCompanyName(user.company_name || '');
      setJobTitle(user.job_title || '');
      setAspirations(user.motivation?.aspirations || '');
      setGoals(user.motivation?.goals || '');
      setGrowthAreas(user.motivation?.growth_areas || '');
      setEmailFrequency(user.email_frequency || 'daily');
      setLoading(false);
    }
  }, [user]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaved(false);
    try {
      const token = localStorage.getItem('auth_token');
      const resp = await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          company_name: companyName,
          job_title: jobTitle,
        }),
      });
      if (resp.ok) {
        setSaved(true);
        await refreshUser();
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (err) {
      console.error('Failed to save profile:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMotivation = async (e) => {
    e.preventDefault();
    setSavingMotivation(true);
    setSavedMotivation(false);
    try {
      const token = localStorage.getItem('auth_token');
      const resp = await fetch(`${API_URL}/api/auth/motivation`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          aspirations: aspirations || null,
          goals: goals || null,
          growth_areas: growthAreas || null,
        }),
      });
      if (resp.ok) {
        setSavedMotivation(true);
        await refreshUser();
        setTimeout(() => setSavedMotivation(false), 3000);
      }
    } catch (err) {
      console.error('Failed to save motivation:', err);
    } finally {
      setSavingMotivation(false);
    }
  };

  const handleSaveEmailPreferences = async (newFrequency) => {
    setSavingEmail(true);
    setSavedEmail(false);
    try {
      const token = localStorage.getItem('auth_token');
      const resp = await fetch(`${API_URL}/api/auth/email-preferences`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          email_frequency: newFrequency,
        }),
      });
      if (resp.ok) {
        setEmailFrequency(newFrequency);
        setSavedEmail(true);
        await refreshUser();
        setTimeout(() => setSavedEmail(false), 3000);
      }
    } catch (err) {
      console.error('Failed to save email preferences:', err);
    } finally {
      setSavingEmail(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950" data-testid="settings-page">
      <header className="border-b border-zinc-800 sticky top-0 bg-zinc-950/95 backdrop-blur z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/dashboard" className="text-zinc-400 hover:text-white transition-colors" data-testid="back-to-dashboard">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-2">
            <SettingsIcon className="w-5 h-5 text-zinc-400" />
            <h1 className="text-lg font-semibold text-white">Settings</h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Profile Section */}
        <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mb-6">
          <h2 className="text-base font-semibold text-white mb-1">Your Profile</h2>
          <p className="text-zinc-500 text-sm mb-6">
            This helps us tailor the companion experience to your context.
          </p>

          <div className="space-y-2 mb-6 p-4 bg-zinc-800/50 rounded-lg">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-zinc-500 w-16">Email</span>
              <span className="text-white">{user?.email}</span>
            </div>
            {user?.name && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-zinc-500 w-16">Name</span>
                <span className="text-white">{user.name}</span>
              </div>
            )}
          </div>

          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label className="text-zinc-400 text-xs font-medium mb-1.5 flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5" />
                Company / Organisation
              </label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="e.g. Acme Corp"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2.5 text-white text-sm placeholder:text-zinc-600 focus:border-zinc-500 focus:outline-none transition-colors"
                data-testid="settings-company-input"
              />
              <p className="text-zinc-600 text-xs mt-1">
                Optional. Used to personalise your experience.
              </p>
            </div>

            <div>
              <label className="text-zinc-400 text-xs font-medium mb-1.5 flex items-center gap-1.5">
                <Briefcase className="w-3.5 h-3.5" />
                Job Title / Role
              </label>
              <input
                type="text"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                placeholder="e.g. Engineering Manager"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2.5 text-white text-sm placeholder:text-zinc-600 focus:border-zinc-500 focus:outline-none transition-colors"
                data-testid="settings-jobtitle-input"
              />
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg text-sm transition-colors disabled:opacity-50"
                data-testid="settings-save-button"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : saved ? (
                  <Check className="w-4 h-4" />
                ) : null}
                {saving ? 'Saving...' : saved ? 'Saved' : 'Save Changes'}
              </button>
              {saved && (
                <span className="text-green-400 text-sm">Profile updated</span>
              )}
            </div>
          </form>
        </section>

        {/* Companion Focus Section */}
        <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mb-6" data-testid="persona-settings-section">
          <h2 className="text-base font-semibold text-white mb-1">Companion Focus</h2>
          <p className="text-zinc-500 text-sm mb-4">
            This determines how your companion thinks and the kind of support it provides.
          </p>
          
          {showPersonaSelector ? (
            <PersonaSelector
              currentPersonaId={user?.persona_id}
              isModal={false}
              onComplete={async () => {
                setShowPersonaSelector(false);
                await refreshUser();
              }}
            />
          ) : (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-zinc-200 text-sm font-medium" data-testid="current-persona-name">
                  {user?.persona_id ? {
                    it_leadership: 'IT Leadership Companion',
                    healthcare_it: 'Healthcare IT Companion',
                    unemployed_it: 'IT Career Transition Companion',
                    founders: 'Founder Companion',
                  }[user.persona_id] || 'Not selected' : 'Not selected'}
                </p>
              </div>
              <button
                onClick={() => setShowPersonaSelector(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm text-zinc-400 hover:text-white border border-zinc-700 hover:border-zinc-500 rounded-lg transition-colors"
                data-testid="change-persona-button"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Change
              </button>
            </div>
          )}
        </section>

        {/* Goals & Motivation Section */}
        <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mb-6" data-testid="motivation-section">
          <h2 className="text-base font-semibold text-white mb-1">Your Goals & Motivation</h2>
          <p className="text-zinc-500 text-sm mb-6">
            This helps your companion understand what matters to you. Update this whenever your priorities shift.
          </p>

          <form onSubmit={handleSaveMotivation} className="space-y-4">
            <div>
              <label className="text-zinc-400 text-xs font-medium mb-1.5 flex items-center gap-1.5">
                <Compass className="w-3.5 h-3.5 text-blue-400" />
                What would you like to achieve?
              </label>
              <textarea
                value={aspirations}
                onChange={(e) => setAspirations(e.target.value)}
                placeholder="e.g. Build confidence leading a larger team, develop a product that makes a difference..."
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2.5 text-white text-sm placeholder:text-zinc-600 focus:border-zinc-500 focus:outline-none transition-colors resize-none"
                rows={3}
                data-testid="settings-aspirations-input"
              />
            </div>

            <div>
              <label className="text-zinc-400 text-xs font-medium mb-1.5 flex items-center gap-1.5">
                <Target className="w-3.5 h-3.5 text-green-400" />
                What are your goals right now?
              </label>
              <textarea
                value={goals}
                onChange={(e) => setGoals(e.target.value)}
                placeholder="e.g. Have better one-to-ones, get promoted this year, improve work-life balance..."
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2.5 text-white text-sm placeholder:text-zinc-600 focus:border-zinc-500 focus:outline-none transition-colors resize-none"
                rows={3}
                data-testid="settings-goals-input"
              />
            </div>

            <div>
              <label className="text-zinc-400 text-xs font-medium mb-1.5 flex items-center gap-1.5">
                <TrendingUp className="w-3.5 h-3.5 text-amber-400" />
                What do you believe you could do better?
              </label>
              <textarea
                value={growthAreas}
                onChange={(e) => setGrowthAreas(e.target.value)}
                placeholder="e.g. Delegating instead of doing everything myself, making decisions with less hesitation..."
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2.5 text-white text-sm placeholder:text-zinc-600 focus:border-zinc-500 focus:outline-none transition-colors resize-none"
                rows={3}
                data-testid="settings-growth-input"
              />
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                type="submit"
                disabled={savingMotivation}
                className="flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg text-sm transition-colors disabled:opacity-50"
                data-testid="settings-save-motivation-button"
              >
                {savingMotivation ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : savedMotivation ? (
                  <Check className="w-4 h-4" />
                ) : null}
                {savingMotivation ? 'Saving...' : savedMotivation ? 'Saved' : 'Save Goals'}
              </button>
              {savedMotivation && (
                <span className="text-green-400 text-sm" data-testid="motivation-saved-msg">Goals updated</span>
              )}
            </div>
          </form>
        </section>

        {/* Email Preferences */}
        <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-6" data-testid="email-preferences-section">
          <div className="flex items-center gap-2 mb-1">
            <Mail className="w-4 h-4 text-purple-400" />
            <h2 className="text-base font-semibold text-white">Email Preferences</h2>
          </div>
          <p className="text-zinc-500 text-sm mb-5">How often would you like to hear from your Companion?</p>
          
          <div className="space-y-3">
            {/* Daily */}
            <button
              onClick={() => handleSaveEmailPreferences('daily')}
              disabled={savingEmail}
              className={`w-full flex items-center gap-4 p-4 rounded-lg border transition-all ${
                emailFrequency === 'daily' 
                  ? 'bg-purple-500/10 border-purple-500/30 text-white' 
                  : 'bg-zinc-800/50 border-zinc-700 text-zinc-300 hover:border-zinc-600'
              }`}
              data-testid="email-freq-daily"
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                emailFrequency === 'daily' ? 'bg-purple-500/20' : 'bg-zinc-700'
              }`}>
                <Calendar className={`w-5 h-5 ${emailFrequency === 'daily' ? 'text-purple-400' : 'text-zinc-400'}`} />
              </div>
              <div className="flex-1 text-left">
                <p className="font-medium">Daily prompts</p>
                <p className="text-sm text-zinc-500">Morning check-ins to keep you reflecting</p>
              </div>
              {emailFrequency === 'daily' && (
                <Check className="w-5 h-5 text-purple-400" />
              )}
            </button>

            {/* Weekly */}
            <button
              onClick={() => handleSaveEmailPreferences('weekly')}
              disabled={savingEmail}
              className={`w-full flex items-center gap-4 p-4 rounded-lg border transition-all ${
                emailFrequency === 'weekly' 
                  ? 'bg-purple-500/10 border-purple-500/30 text-white' 
                  : 'bg-zinc-800/50 border-zinc-700 text-zinc-300 hover:border-zinc-600'
              }`}
              data-testid="email-freq-weekly"
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                emailFrequency === 'weekly' ? 'bg-purple-500/20' : 'bg-zinc-700'
              }`}>
                <Clock className={`w-5 h-5 ${emailFrequency === 'weekly' ? 'text-purple-400' : 'text-zinc-400'}`} />
              </div>
              <div className="flex-1 text-left">
                <p className="font-medium">Weekly touchpoint</p>
                <p className="text-sm text-zinc-500">One prompt on Monday mornings</p>
              </div>
              {emailFrequency === 'weekly' && (
                <Check className="w-5 h-5 text-purple-400" />
              )}
            </button>

            {/* Ad-hoc */}
            <button
              onClick={() => handleSaveEmailPreferences('adhoc')}
              disabled={savingEmail}
              className={`w-full flex items-center gap-4 p-4 rounded-lg border transition-all ${
                emailFrequency === 'adhoc' 
                  ? 'bg-purple-500/10 border-purple-500/30 text-white' 
                  : 'bg-zinc-800/50 border-zinc-700 text-zinc-300 hover:border-zinc-600'
              }`}
              data-testid="email-freq-adhoc"
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                emailFrequency === 'adhoc' ? 'bg-purple-500/20' : 'bg-zinc-700'
              }`}>
                <MessageSquare className={`w-5 h-5 ${emailFrequency === 'adhoc' ? 'text-purple-400' : 'text-zinc-400'}`} />
              </div>
              <div className="flex-1 text-left">
                <p className="font-medium">Only when I reach out</p>
                <p className="text-sm text-zinc-500">I'll respond when you email me</p>
              </div>
              {emailFrequency === 'adhoc' && (
                <Check className="w-5 h-5 text-purple-400" />
              )}
            </button>
          </div>

          {savedEmail && (
            <p className="text-green-400 text-sm mt-4" data-testid="email-saved-msg">
              Preference saved
            </p>
          )}
          {savingEmail && (
            <p className="text-zinc-400 text-sm mt-4 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving...
            </p>
          )}
        </section>

        {/* Connected Channels */}
        <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mb-6" data-testid="connected-channels-section">
          <ConnectedChannels />
        </section>

        {/* Calendar Integration */}
        <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mb-6" data-testid="calendar-settings-section">
          <CalendarSettings />
        </section>

        {/* Account Info */}
        <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mb-6">
          <h2 className="text-base font-semibold text-white mb-1">Account</h2>
          <div className="space-y-2 mt-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-zinc-500">AI Companion</span>
              <span className={user?.has_ai_access ? 'text-green-400' : 'text-zinc-400'}>
                {user?.has_ai_access ? (user?.ai_access_type === 'purchased' ? 'Active (Paid)' : 'Active (Trial)') : 'Not Active'}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-zinc-500">Books Owned</span>
              <span className="text-white">{user?.books_owned?.length || 0}</span>
            </div>
          </div>
        </section>

        {/* SEC.10: Privacy & data — GDPR account deletion + data export */}
        <DeleteAccountSection userEmail={user?.email} onStatusChange={refreshUser} />
      </main>
    </div>
  );
};

export default SettingsPage;
